import React from "react";
import { useParams, Link } from "react-router-dom";
// Import the hero image from your local assets
import RentalSearch from "./RentalSearch";
import heroImg from "../../assets/hero.jpg";

// --- SVG Icon Helpers ---
const CheckIcon = ({ className = "w-4 h-4" }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const PhoneIcon = ({ className = "w-4 h-4" }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
    />
  </svg>
);

const LocationIcon = ({ className = "w-4 h-4" }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.243-4.243a8 8 0 1111.314 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

export default function BrandRentalPage() {
  const { brandId } = useParams();

  const brandName = brandId
    ? brandId.charAt(0).toUpperCase() + brandId.slice(1).toLowerCase()
    : "Brand";

  const handlePhoneClick = () => {
    if (window.gtag) {
      window.gtag("config", "AW-18343552397/3FAKCIr6l9ccEI3L8apE", {
        phone_conversion_number: "1-877-991-0909",
      });
    }
  };

  return (
    <div className="bg-white pb-20 font-sans text-slate-800 overflow-x-hidden">
      {/* ================= HERO SECTION ================= */}
      <section className="mx-auto max-w-[1580px] px-4 py-8 md:px-8 lg:py-16 xl:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center lg:gap-16">
          {/* Left Content */}
          <div className="max-w-xl mx-auto lg:mx-0 w-full">
            <p className="mb-3 md:mb-4 flex items-center text-[10px] md:text-[11px] font-extrabold uppercase tracking-widest text-[#e8211b]">
              <span className="mr-2 text-[14px] leading-none">•</span> CAR
              RENTAL BOOKING ASSISTANCE
            </p>
            {/* Responsive Heading */}
            <h1 className="mb-4 md:mb-6 text-4xl sm:text-5xl font-black leading-[1.1] text-[#081c3a] md:text-6xl lg:text-[64px]">
              {brandName} Car Rental Options
            </h1>
            <p className="mb-6 md:mb-8 text-[15px] sm:text-[17px] leading-relaxed text-[#3a4759]">
              Request rental quote assistance for {brandName} vehicle category
              comparisons, including airport pickup, business travel, city
              rentals, family trips, SUVs, luxury vehicles, and extended
              rentals.
            </p>

            {/* Buttons - Stack on mobile, inline on tablet+ */}
            <div className="mb-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
              <a
                href="#rental-request"
                className="text-center rounded-full bg-[#e8211b] px-6 py-3.5 md:py-4 text-[13px] font-extrabold uppercase tracking-wide text-white transition-colors hover:bg-red-700 shadow-[0_8px_20px_rgba(232,33,27,0.25)]"
              >
                Compare Rental Options
              </a>
              <a
                href="tel:+18779910909"
                onClick={handlePhoneClick}
                className="flex justify-center items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3.5 md:py-4 text-[13px] font-extrabold uppercase tracking-wide text-[#081c3a] transition-all hover:border-[#081c3a] hover:bg-slate-50 shadow-sm"
              >
                <PhoneIcon className="h-4 w-4" /> Call 1-877-991-0909
              </a>
            </div>

            {/* Feature Pills */}
            <div className="flex flex-wrap gap-2 md:gap-3">
              {[
                "Simple Reservations",
                "Multiple Vehicle Options",
                "Customer-Focused Support",
              ].map((feat) => (
                <div
                  key={feat}
                  className="flex items-center gap-2 rounded-full border border-slate-100 bg-white px-3 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm font-bold text-[#081c3a] shadow-sm"
                >
                  <CheckIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#e8211b]" />
                  {feat}
                </div>
              ))}
            </div>
          </div>

          {/* Right Content / Image */}
          <div className="relative w-full mt-6 lg:mt-0">
            {/* Decorative background blob */}
            <div className="hidden lg:block absolute -left-6 top-10 h-64 w-24 rounded-[40px] bg-red-50 opacity-50 blur-xl"></div>

            <div className="relative overflow-hidden rounded-2xl md:rounded-[32px] shadow-xl md:shadow-2xl">
              {/* Dynamic Image Height */}
              <img
                src={heroImg}
                alt={`${brandName} Rental Car`}
                className="h-[350px] sm:h-[400px] lg:h-[500px] w-full object-cover"
              />

              {/* Floating Support Card - Responsive Positioning */}
              <div className="absolute bottom-4 left-4 right-4 sm:right-auto sm:bottom-6 sm:left-6 sm:max-w-[320px] rounded-xl sm:rounded-2xl bg-white/95 backdrop-blur-sm sm:bg-white p-4 sm:p-6 shadow-xl">
                <p className="mb-1 sm:mb-2 text-[9px] sm:text-[10px] font-extrabold uppercase tracking-widest text-[#e8211b]">
                  RESERVATION SUPPORT
                </p>
                <p className="text-[14px] sm:text-[16px] lg:text-[17px] font-extrabold leading-tight text-[#081c3a]">
                  Plan changes, cancellations where permitted, and rental
                  questions handled clearly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="bg-slate-100 p-5">
        <RentalSearch />
      </div>

      {/* ================= DETAILS SECTION ================= */}
      <section className="mx-auto max-w-[1580px] px-4 md:px-8 mt-8 lg:mt-12">
        <div className="mx-auto max-w-5xl">
          <p className="mb-2 md:mb-3 text-[10px] md:text-[11px] font-extrabold uppercase tracking-widest text-[#e8211b]">
            {brandName.toUpperCase()} BRAND RENTAL GUIDE
          </p>
          <h2 className="mb-4 md:mb-6 text-2xl sm:text-3xl font-extrabold leading-tight text-[#081c3a] md:text-[34px]">
            Compare {brandName} Car Rental Options for Business and Leisure
            Travel
          </h2>

          <div className="mb-6 md:mb-8 space-y-3 md:space-y-4 text-[14px] sm:text-[15px] leading-relaxed text-[#3a4759]">
            <p>
              Need to compare <strong>{brandName} car rental options</strong>{" "}
              for an airport arrival, downtown pickup, business meeting, family
              trip, or extended rental plan? We help travelers request quote
              assistance for new rental inquiries based on location and travel
              dates.
            </p>
            <p>
              {brandName}-related rental comparisons may include economy cars,
              compact cars, fullsize vehicles, <strong>SUV rentals</strong>,
              luxury cars, vans, and pickup trucks for travelers who want
              flexible choices before booking.
            </p>
            <p>
              We provide independent booking assistance for new rental
              inquiries. We are not affiliated with, endorsed by, or acting on
              behalf of {brandName}, any airport, airline, or rental company.
            </p>
          </div>

          {/* Info Grid - Adaptive Borders */}
          <div className="mb-8 grid grid-cols-1 divide-y divide-slate-200 rounded-xl border border-slate-200 bg-[#fbfcfd] sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-4 lg:divide-x">
            <div className="p-4 sm:p-5 border-b sm:border-b-0 border-slate-200 lg:border-b-0">
              <p className="mb-1 text-[10px] font-extrabold uppercase tracking-widest text-[#e8211b]">
                Brand
              </p>
              <p className="font-extrabold text-[#081c3a]">{brandName}</p>
            </div>
            <div className="p-4 sm:p-5 border-b sm:border-b-0 border-slate-200 lg:border-b-0">
              <p className="mb-1 text-[10px] font-extrabold uppercase tracking-widest text-[#e8211b]">
                Popular For
              </p>
              <p className="font-extrabold text-[#081c3a]">
                Business, airport & leisure
              </p>
            </div>
            {/* Force border top on small screens where columns wrap to next row */}
            <div className="p-4 sm:p-5 border-t sm:border-t lg:border-t-0 lg:border-l border-slate-200">
              <p className="mb-1 text-[10px] font-extrabold uppercase tracking-widest text-[#e8211b]">
                Vehicle Options
              </p>
              <p className="font-extrabold text-[#081c3a]">
                Compact, SUV, luxury, vans
              </p>
            </div>
            <div className="p-4 sm:p-5 border-t sm:border-t-0 lg:border-t-0 border-slate-200">
              <p className="mb-1 text-[10px] font-extrabold uppercase tracking-widest text-[#e8211b]">
                Rental Needs
              </p>
              <p className="font-extrabold text-[#081c3a]">
                City, airport & long-term
              </p>
            </div>
          </div>

          {/* Location Tags */}
          <div className="mb-10 md:mb-12 flex flex-wrap gap-2">
            {[
              `${brandName} rental quote`,
              "Business rentals",
              "Airport pickup",
              "City travel",
              "Family rentals",
              "SUV requests",
              "Luxury rentals",
              "Extended rentals",
            ].map((tag, idx) => (
              <span
                key={idx}
                className="flex items-center gap-1.5 md:gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 md:px-3 md:py-1.5 text-[12px] md:text-[13px] font-bold text-[#081c3a] shadow-sm"
              >
                <LocationIcon className="h-3 w-3 md:h-3.5 md:w-3.5 text-slate-400" />{" "}
                {tag}
              </span>
            ))}
          </div>

          <h3 className="mb-4 md:mb-5 text-xl md:text-[22px] font-extrabold text-[#081c3a]">
            A clearer way to compare {brandName} rental options
          </h3>

          {/* Comparison Cards Grid */}
          <div className="mb-10 md:mb-12 grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                title: "Corporate and Work Travel",
                desc: "Request options for conferences, client visits, temporary work assignments, and city-to-city travel.",
              },
              {
                title: "Vehicle Fit Guidance",
                desc: "Compare compact cars for city parking, SUVs for luggage, luxury vehicles, vans, and standard sedans.",
              },
              {
                title: "Short or Extended Trips",
                desc: "Useful for daily errands, weekend travel, weekly rentals, and longer project-based rental needs.",
              },
              {
                title: "Quote Request Review",
                desc: "Share travel dates, pickup plans, and vehicle preferences to request rental quote details.",
              },
            ].map((card, idx) => (
              <div
                key={idx}
                className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm"
              >
                <div className="mb-2 md:mb-3 flex items-start sm:items-center gap-2 text-[#081c3a]">
                  <LocationIcon className="mt-0.5 sm:mt-0 h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                  <h4 className="text-[14px] sm:text-[15px] font-bold">
                    {card.title}
                  </h4>
                </div>
                <p className="text-[12px] sm:text-[13px] leading-relaxed text-[#4b6a97]">
                  {card.desc}
                </p>
              </div>
            ))}
          </div>

          <hr className="mb-6 md:mb-8 border-slate-200" />

          {/* Popular Use Cases */}
          <h3 className="mb-2 md:mb-3 text-xl md:text-[22px] font-extrabold text-[#081c3a]">
            Popular {brandName} Rental Use Cases
          </h3>
          <p className="mb-4 md:mb-5 text-[14px] md:text-[15px] text-[#3a4759]">
            Travelers often compare {brandName} rental options for work trips,
            airport pickup, weekend travel, family visits, city driving, and
            long-term transportation.
          </p>
          <div className="mb-10 md:mb-12 flex flex-wrap gap-2 md:gap-3">
            {[
              "Work assignments",
              "Airport arrivals",
              "Downtown pickup",
              "Family travel",
              "Luxury requests",
              "SUV trips",
              "Weekly travel",
              "Extended stays",
            ].map((tag, idx) => (
              <span
                key={idx}
                className="rounded-md border border-slate-200 bg-white px-2.5 py-1 md:px-3 md:py-1.5 text-[12px] md:text-[13px] font-bold text-[#081c3a]"
              >
                {tag}
              </span>
            ))}
          </div>

          <hr className="mb-6 md:mb-8 border-slate-200" />

          {/* Helpful Rental Pages */}
          <h3 className="mb-2 md:mb-3 text-xl md:text-[22px] font-extrabold text-[#081c3a]">
            Helpful Rental Pages
          </h3>
          <p className="mb-4 md:mb-5 text-[14px] md:text-[15px] text-[#3a4759]">
            {brandName} comparison visitors may also review airport rentals,
            long-term rentals, SUV options, luxury vehicles, and popular pickup
            pages.
          </p>
          <div className="mb-10 md:mb-12 flex flex-wrap gap-2 md:gap-3">
            {[
              "Car Rental",
              "Long-Term Car Rental",
              "New York Car Rental",
              "LAX Airport Rental",
            ].map((tag, idx) => (
              <Link
                to="#"
                key={idx}
                className="rounded-md border border-slate-200 bg-white px-2.5 py-1 md:px-3 md:py-1.5 text-[12px] md:text-[13px] font-bold text-[#081c3a] hover:bg-slate-50 hover:text-[#e8211b] transition-colors"
              >
                {tag}
              </Link>
            ))}
          </div>

          <hr className="mb-6 md:mb-8 border-slate-200" />

          {/* Tips Section */}
          <p className="mb-2 text-[10px] md:text-[11px] font-extrabold uppercase tracking-widest text-[#e8211b]">
            {brandName.toUpperCase()} RENTAL TIPS
          </p>
          <h3 className="mb-4 md:mb-6 text-xl md:text-[22px] font-extrabold text-[#081c3a]">
            Tips Before Requesting {brandName} Rental Options
          </h3>

          <div className="grid grid-cols-1 gap-x-8 gap-y-3 md:gap-y-4 sm:grid-cols-2 text-[13px] md:text-[14px] text-[#3a4759]">
            {[
              "Confirm whether airport pickup or city pickup best fits your schedule.",
              "Choose a vehicle class based on parking, luggage, passengers, and distance.",
              "Ask about longer rental durations if your business trip may extend.",
              "Compare SUV or van requests early for family and group travel.",
              "Review taxes, fees, mileage, availability, and provider terms before booking.",
              "Use this page for new rental quote assistance only.",
            ].map((tip, idx) => (
              <div key={idx} className="flex items-start gap-2.5 md:gap-3">
                <CheckIcon className="mt-0.5 h-3.5 w-3.5 md:h-4 md:w-4 flex-shrink-0 text-[#081c3a]" />
                <span className="leading-relaxed">{tip}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
